import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  bubbleAnchor,
  isBubbleFailed,
} from '@/components/admin/sessions/conversation/conversation-fil';
import { FoldToggle, useFold } from '@/components/admin/sessions/walk/walk-fold';
import { isTicked, pageExit, pageWindowLabel } from '@/components/admin/sessions/walk/walk-labels';
import { WalkNoyauSection } from '@/components/admin/sessions/walk/walk-noyau-section';
import { costLabel, kindedValueLabel } from '@/lib/session-labels';
import { type WalkNoyauSite, noyauSiteSummary, portraitCut, takenCard } from '@/lib/session-noyau';
import { pageAnchor } from '@/lib/session-toc';
import { cn } from '@/lib/utils';
import {
  type SessionConversationBubble,
  type SessionNoyauReading,
  type SessionStatus,
  type SessionVariableKind,
  type SessionWalkPage,
  noyauOf,
} from '@matter_extractor/shared';
import { Button, Icon } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';

/**
 * One page the session traversed (§12.2): its rank, its name, its clock, what
 * was typed on it, and the button that left it.
 *
 * ## One line, since #223
 *
 * An ordinary page holds on ONE line (maquette de la vue session): the rank in a
 * pastille of 26 px, the name, then ONE sentence of detail - the values where they
 * were typed (« prénom Nicolas Hulot · email laissé vide · consentement coché », or
 * « Aucune saisie »), then how it was left - and the clock on the right. Nothing was
 * dropped: a long sentence wraps rather than being cut, and the marks the suites and
 * the Variables tab read (`data-visit`, the anchor of the sommaire, the ring) are
 * those of the card it replaced. The protocol page keeps its own card.
 *
 * A page of CHOICE of the noyau says its cards in the same sentence (« Premier choix :
 * carte prise PGS, sans effet (écart 14 > 5) · retenu PSG ») and unfolds what it always
 * drew - the two cards, their texts, the version of the texts, the line of the gap -
 * behind « Voir les cartes ▾ ». A page of PORTRAIT shows the hook and the first
 * paragraph, and « Lire tout le portrait ▾ » the rest. Both are postures of reading,
 * the component's own state like the blocks of analysis (#219), and a folded body is
 * not mounted.
 *
 * ## The values are shown HERE and nowhere else
 *
 * That is the whole reason the reader of §12.2 became a walk: « un écran qui
 * n'ouvrait que la conversation montrait les valeurs saisies loin de l'endroit
 * où elles avaient été tapées ». So a page carries its own pairs, in the order
 * the server grouped them, and the screen has no second list of variables.
 *
 * ## The rank is the rank in the WALK, not in the timeline
 *
 * « Page 2 » is the second page this session entered, and a page entered twice
 * gets two ranks. A number read off the frozen experience would answer a
 * different question - where the page sits in the design - which is the one this
 * screen is not about.
 *
 * ## A page played in the conversation says so (prolongation.md §7, issue #186)
 *
 * « Jouée dans la conversation » - a pill in the header and a line in the body
 * that leads to its bubble in the Conversation screen (`#bulle-<visite>`, which
 * that screen scrolls to and lights). Everything else reads as on any page: the
 * values, the card taken, the button that left it. The bubble is matched to the
 * card by the VISIT, the rank in the walk both screens print as « Page 7 ».
 *
 * A page with no value is not an empty card: it says « Aucune saisie » - unless it
 * carries a Noyau block (noyau.md §9.1, #152), whose cards or portrait are then
 * what the page shows.
 */

export interface WalkPageCardProps {
  readonly page: SessionWalkPage;
  /** 1-based rank in the walk. */
  readonly rank: number;
  /** Only `pageExit` reads it: it separates « terminée » from « encore là ». */
  readonly status: SessionStatus;
  /**
   * The Noyau blocks this page carried, the choice first (`noyauSitesOfPage`),
   * and the reading they are drawn from; absent on every other page.
   */
  readonly noyau?: {
    readonly sites: readonly WalkNoyauSite[];
    readonly reading: SessionNoyauReading;
  } | null;
  /**
   * The nature of each variable the inventory declares, by the key it is stored
   * under (#176): what lets a Note read « 4 / 5 » where the walk's own values
   * carry no kind. Absent, every value reads as it is stored.
   */
  readonly kinds?: ReadonlyMap<string, SessionVariableKind>;
  /** Ringed a moment: the card a line of the Variables tab led back to (#172). */
  readonly flash?: boolean;
  /**
   * The bubble of this visit when the page was played IN the conversation
   * (#186), with the session whose Conversation screen the line leads to;
   * absent on every other page.
   */
  readonly bubble?: {
    readonly sessionId: string;
    readonly bubble: SessionConversationBubble;
  } | null;
}

export function WalkPageCard({
  page,
  rank,
  status,
  noyau = null,
  kinds,
  flash = false,
  bubble = null,
}: WalkPageCardProps) {
  // The choice is what the page was LEFT by, so its card taken names the exit.
  const choice = noyau?.sites.find((site) => site.choice !== null)?.choice ?? null;
  const taken = choice === null ? null : takenCard(choice);
  const exit = pageExit(page, status, taken === null ? null : noyauOf(taken.noyau).name);

  const cards = useFold(false);
  const portrait = useFold(false);
  const sites = noyau?.sites ?? [];
  const choiceSites = sites.filter((site) => site.mode !== 'portrait');
  // The portrait is cut only when there is one to read: no noyau retained, no portrait.
  const servedPortrait =
    noyau === null || noyau.reading.retained === null ? null : noyau.reading.portrait;
  const portraitIsCut =
    sites.some((site) => site.mode === 'portrait') &&
    servedPortrait !== null &&
    portraitCut(servedPortrait).cut;

  const facts: ReactNode[] = [];
  if (noyau !== null) {
    for (const site of choiceSites) {
      facts.push(
        <span
          key={`site-${site.blockId}`}
          data-testid={SESSIONS_TEST_IDS.walkNoyauPageSummary}
          data-mode={site.mode}
        >
          {noyauSiteSummary(site, noyau.reading)}
        </span>,
      );
    }
  }
  if (page.values.length > 0) {
    facts.push(<PageValues key="values" values={page.values} kinds={kinds} />);
  } else if (noyau === null) {
    facts.push(<span key="values">Aucune saisie</span>);
  }
  // A page of the noyau leaves by its own row, under its cards; the others say it here.
  if (noyau === null) {
    facts.push(<PageExit key="exit" exit={exit} lead={facts.length > 0} />);
  }

  return (
    <article
      id={pageAnchor(rank)}
      data-testid={SESSIONS_TEST_IDS.walkPage}
      data-page-id={page.pageId}
      // The index of the visit, which the Variables tab leads back to (#172).
      data-visit={rank - 1}
      data-flash={flash ? 'true' : undefined}
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1.5 overflow-hidden rounded-md border border-gray-200 bg-white px-4 py-2.5',
        flash ? 'pms-flash' : null,
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-[26px] shrink-0 items-center justify-center rounded-md bg-gray-100 font-bold text-caption text-gray-900"
      >
        {rank}
      </span>
      <h3 className="font-bold text-body text-gray-900">
        <span className="sr-only">Page {rank} : </span>
        {page.name}
      </h3>
      {bubble === null ? null : (
        <span
          data-testid={SESSIONS_TEST_IDS.walkBubble}
          className="inline-flex items-center gap-1 rounded-full bg-white px-2 font-semibold text-bleu-nuit text-micro ring-1 ring-bleu-nuit/20 ring-inset"
        >
          <Icon name="chat_bubble" size={14} />
          jouée dans la conversation
        </span>
      )}
      {facts.length === 0 ? null : (
        <span
          data-testid={SESSIONS_TEST_IDS.walkPageDetail}
          className="min-w-0 text-caption text-gray-700"
        >
          {facts.map((fact, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the facts of one line never move.
            <Fragment key={index}>
              {index === 0 ? null : <span aria-hidden="true"> · </span>}
              {fact}
            </Fragment>
          ))}
        </span>
      )}
      <span className="ml-auto whitespace-nowrap text-gray-600 text-caption tabular-nums">
        {pageWindowLabel(page)}
      </span>

      {noyau === null
        ? null
        : sites.map((site) => (
            <WalkNoyauSection
              key={site.blockId}
              site={site}
              noyau={noyau.reading}
              cards={cards}
              portrait={portrait}
              portraitIsCut={portraitIsCut}
              bodyId={site.blockId === choiceSites[0]?.blockId ? cards.bodyId : undefined}
            />
          ))}

      {bubble === null ? null : (
        <p className="basis-full pl-[38px] text-caption text-gray-600">
          {isBubbleFailed(bubble.bubble)
            ? 'L’appel de la bulle a échoué : la page s’est jouée sans bulle'
            : 'Amenée par une bulle de l’interviewer'}
          {' · '}
          {costLabel(bubble.bubble.costEur)}
          {' · '}
          <Link
            data-testid={SESSIONS_TEST_IDS.walkBubbleLink}
            to="/admin/sessions/$sessionId/protocole"
            params={{ sessionId: bubble.sessionId }}
            hash={bubbleAnchor(bubble.bubble.visitIndex)}
            className="font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            Voir la bulle dans la conversation
          </Link>
        </p>
      )}

      {noyau === null ? null : (
        <div className="flex basis-full flex-wrap items-center gap-x-3 gap-y-1.5 pl-[38px] text-caption text-gray-700">
          {choiceSites.length === 0 ? null : (
            <FoldToggle
              fold={cards}
              closedLabel="Voir les cartes ▾"
              testId={SESSIONS_TEST_IDS.walkNoyauPageToggle}
            />
          )}
          {portraitIsCut ? (
            <Button
              type="button"
              variant="accent"
              size="sm"
              data-testid={SESSIONS_TEST_IDS.walkNoyauPortraitToggle}
              aria-expanded={portrait.open}
              aria-controls={portrait.open ? portrait.bodyId : undefined}
              onClick={portrait.toggle}
            >
              {portrait.open ? 'Replier le portrait ▴' : 'Lire tout le portrait ▾'}
            </Button>
          ) : null}
          <PageExit exit={exit} lead={choiceSites.length > 0 || portraitIsCut} />
        </div>
      )}
    </article>
  );
}

/**
 * The values typed on the page, as the sentence of the line: « prénom Nicolas Hulot · email
 * laissé vide · consentement coché ». Still a description list - each pair a term and its
 * definition, in the order the server grouped them - only drawn inline, so a screen reader
 * hears the same pairs the card had and a page with many values wraps instead of truncating.
 */
function PageValues({
  values,
  kinds,
}: {
  values: SessionWalkPage['values'];
  kinds: WalkPageCardProps['kinds'];
}) {
  return (
    <dl className="inline">
      {values.map((value) => (
        <div key={value.blockId} className="inline not-last:after:content-['_·_']">
          <dt className="inline text-gray-600">{value.name}</dt>{' '}
          <dd className="inline break-words font-semibold text-gray-900">
            {isTicked(value.value) ? (
              <span aria-hidden="true" className="mr-1 text-vert-deep">
                ✓
              </span>
            ) : null}
            {kindedValueLabel(value.value, kinds?.get(value.blockId))}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * How the page was left: the same sentence as ever (`pageExit`), the button's words in bold.
 * The first letter is lowered by the style when it follows another fact of the line - the
 * text itself keeps its capital, which is what the suites and the reader of the screen read.
 */
function PageExit({
  exit,
  lead,
}: {
  exit: ReturnType<typeof pageExit>;
  lead: boolean;
}) {
  return (
    <span
      data-testid={SESSIONS_TEST_IDS.walkPageExit}
      className={cn('inline-block', lead ? 'first-letter:lowercase' : null)}
    >
      {exit.text}
      {exit.emphasis === null ? null : (
        <>
          {' '}
          <b className="font-semibold text-gray-900">{exit.emphasis}</b>
        </>
      )}
    </span>
  );
}
